import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, Target, Dumbbell, Brain, Activity, Flame, Sparkles, Trophy,
  ChevronRight, Download, ArrowUpRight, Calendar, MapPin, Hexagon, Shield, Flame as Fire
} from "lucide-react";
import { api } from "@/lib/api";
import { BRAND, PILLARS } from "@/data/brand";
import TeamCard from "@/components/TeamCard";
import TeamLogo from "@/components/TeamLogo";
import SponsorPackageCard from "@/components/SponsorPackageCard";
import ScheduleTable from "@/components/ScheduleTable";
import StandingsTable from "@/components/StandingsTable";
import LeadForm from "@/components/LeadForm";
import Marquee from "@/components/Marquee";
import PageMeta from "@/components/PageMeta";

const PILLAR_ICONS = {
  speed: Zap,
  skill: Target,
  strength: Dumbbell,
  strategy: Brain,
  stamina: Activity,
  spirit: Flame,
  showmanship: Sparkles,
  success: Trophy,
};

/* Tiny <img> wrapper that swaps to fallback on error. Used by pillar cards
   so that while the AI-generated pillar image is still being produced, the
   Unsplash fallback shows seamlessly. */
function PillarBg({ src, fallbackSrc }) {
  const [imgSrc, setImgSrc] = React.useState(src);
  React.useEffect(() => { setImgSrc(src); }, [src]);
  return (
    <img
      src={imgSrc}
      onError={() => { if (imgSrc !== fallbackSrc) setImgSrc(fallbackSrc); }}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700"
    />
  );
}

const HERO_BG = "https://images.unsplash.com/photo-1580748141549-71748dbe0bdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxpY2UlMjBob2NrZXklMjBhY3Rpb258ZW58MHx8fHwxNzgxMjk2MzYxfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [teams, setTeams] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [standings, setStandings] = useState([]);
  const [news, setNews] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/teams"),
      api.get("/sponsorship-tiers"),
      api.get("/schedule"),
      api.get("/standings"),
      api.get("/news"),
      api.get("/shares/leaderboard", { params: { since_days: 7, limit: 5 } }),
    ])
      .then(([t, s, sch, st, n, lb]) => {
        setTeams(t.data);
        setTiers(s.data);
        setSchedule(sch.data);
        setStandings(st.data);
        setNews(n.data);
        setTrending(lb.data?.results || []);
      })
      .catch(() => {});
  }, []);

  const teamsBySlug = Object.fromEntries(teams.map((t) => [t.slug, t]));

  return (
    <div>
      <PageMeta
        title="India's Pro Skate Hockey Franchise League"
        description="PSHL8 — India's next pro skate hockey franchise league. 8 city-based franchises, 9 sponsorship tracks, broadcast-ready, sponsor-first. Become a sponsor, own a franchise, partner with PSHL, or register as a player."
        image="/api/og/home/og-image.png"
      />
      {/* HERO — NHL broadcast-grade */}
      <section data-testid="home-hero" className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#020A1A]">
        {/* Ken-Burns background photo */}
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center md:bg-[78%_center]"
            style={{ backgroundImage: `url(${HERO_BG})` }}
            aria-hidden
          />
        </motion.div>

        {/* Cinematic gradient masks — left side anchors copy, right side stays photo-forward */}
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#020A1A] via-[#020A1A]/90 md:via-[#020A1A]/55 to-transparent" />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#020A1A] via-transparent to-[#020A1A]/35" />
        <div className="absolute inset-0 z-10 speed-lines opacity-25 mix-blend-overlay pointer-events-none" />

        {/* Color glows */}
        <div className="absolute -bottom-24 -left-24 h-[520px] w-[520px] rounded-full blur-[110px] bg-pshl-electric/25 z-10 pointer-events-none" />
        <div className="absolute top-0 right-0 h-[420px] w-[420px] rounded-full blur-[110px] bg-pshl-red/18 z-10 pointer-events-none" />

        <div className="relative z-20 pshl-container py-14 md:py-24 w-full grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-9 lg:col-span-8 space-y-7"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}
              className="inline-flex items-center gap-2 border border-pshl-electric/50 bg-pshl-electric/12 px-4 py-2 backdrop-blur-md"
            >
              <motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2.2, repeat: Infinity }}>
                <Hexagon className="h-4 w-4 text-pshl-electric" />
              </motion.div>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white font-display font-semibold">
                India’s Pro Skate Hockey League · PSHL8
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
              className="font-display text-6xl sm:text-7xl lg:text-8xl xl:text-[7.5rem] text-white leading-[0.88] tracking-tighter uppercase drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]"
            >
              Pro Skate <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70">Hockey</span>{" "}
              <span className="text-pshl-electric drop-shadow-[0_0_22px_rgba(0,85,255,0.45)]">League</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
              className="text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed font-medium drop-shadow-md"
            >
              India’s next professional skate hockey franchise league — built to commercialize,
              promote, and scale the sport through city-based franchises, sponsorships, academies, and media.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }}
              className="font-display text-sm md:text-base uppercase tracking-[0.3em] text-white/85 drop-shadow-md"
            >
              {BRAND.tagline} <span className="text-pshl-red px-2">|</span> <span className="text-pshl-gold">{BRAND.subTagline}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-wrap gap-3 pt-3"
            >
              <Link to="/sponsorships" data-testid="hero-cta-sponsor" className="inline-flex items-center gap-2 bg-pshl-electric hover:bg-white hover:text-[#020A1A] text-white font-display uppercase tracking-[0.16em] text-xs px-7 py-4 shadow-[0_0_22px_rgba(0,85,255,0.45)] hover:shadow-[0_0_28px_rgba(255,255,255,0.55)] transition-all duration-300">
                Become a Sponsor <ChevronRight className="h-4 w-4" />
              </Link>
              <Link to="/sponsorships#franchise" data-testid="hero-cta-franchise" className="inline-flex items-center gap-2 bg-[#041E42]/60 border border-white/25 hover:border-white text-white font-display uppercase tracking-[0.16em] text-xs px-7 py-4 backdrop-blur-md transition-all duration-300">
                Own a Franchise <ChevronRight className="h-4 w-4" />
              </Link>
              <Link to="/partnerships" data-testid="hero-cta-partner" className="inline-flex items-center gap-2 text-white/80 hover:text-pshl-gold font-display uppercase tracking-[0.16em] text-xs px-3 py-4 transition-all duration-300">
                Partner With PSHL
              </Link>
              <Link to="/players" data-testid="hero-cta-player" className="inline-flex items-center gap-2 text-white/80 hover:text-pshl-red font-display uppercase tracking-[0.16em] text-xs px-3 py-4 transition-all duration-300">
                Register as Player
              </Link>
            </motion.div>

            {/* mini stats */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.5 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/12 max-w-3xl mt-6"
            >
              {[
                { k: "8", v: "Franchise Teams" },
                { k: "12+", v: "Launch Cities" },
                { k: "9", v: "Sponsorship Tracks" },
                { k: "∞", v: "Performance" },
              ].map((s) => (
                <div key={s.v} className="relative pl-4">
                  <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-pshl-electric/70 to-transparent" />
                  <div className="font-mono text-3xl md:text-4xl text-white font-bold">{s.k}</div>
                  <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-white/70 mt-1 font-display">{s.v}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee text="PRO · SKATE · HOCKEY · LEAGUE · PSHL8" />

      {/* OFFICIAL BRAND MARK */}
      <section data-testid="section-brand-mark" className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(900px 500px at 50% 50%, rgba(0,85,255,0.18), transparent 70%), radial-gradient(700px 400px at 20% 80%, rgba(255,42,42,0.10), transparent 65%), radial-gradient(700px 400px at 80% 20%, rgba(255,215,0,0.10), transparent 65%)"
        }} />
        <div className="relative pshl-container py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-4">Official Brand Mark</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter leading-[0.95]">
                The <span className="text-gold-metallic">PSHL∞</span> Identity.
              </h2>
              <p className="mt-5 text-white/75 leading-relaxed">
                A silver-and-gold wordmark with an infinity mark that doubles as the figure 8 — Power, Speed, Hustle and Legacy
                woven into a single insignia. Built for jerseys, arenas, broadcast, merchandise and digital.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
                {[
                  { c: "#FF3B30", l: "Power" },
                  { c: "#0055FF", l: "Speed" },
                  { c: "#FFD700", l: "Hustle" },
                ].map((x) => (
                  <div key={x.l} className="border border-white/10 px-3 py-2.5 flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: x.c }} />
                    <span className="text-[11px] uppercase tracking-[0.22em] text-white/85">{x.l}</span>
                  </div>
                ))}
                <div className="col-span-3 border border-pshl-gold/40 bg-pshl-gold/5 px-3 py-2.5 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-pshl-gold" />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-pshl-gold">Legacy · Infinite Performance</span>
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="/pshl8-wordmark.svg" download className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-5 py-3 hover:brightness-110 transition-all">
                  <Download className="h-3.5 w-3.5" /> Wordmark SVG
                </a>
                <a href="/pshl8-brand.png" download className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 transition-all">
                  <Download className="h-3.5 w-3.5" /> Brand Poster PNG
                </a>
                <a href="/pshl8-hero-poster.png" download className="inline-flex items-center gap-2 border border-pshl-gold/40 hover:border-pshl-gold text-pshl-gold hover:bg-pshl-gold/10 font-display uppercase tracking-[0.18em] text-xs px-5 py-3 transition-all">
                  <Download className="h-3.5 w-3.5" /> Hero Keyart
                </a>
                <Link to="/media" className="inline-flex items-center gap-2 text-white/75 hover:text-pshl-electric font-display uppercase tracking-[0.18em] text-xs px-5 py-3">
                  Full Press Kit <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2 relative">
              <div className="absolute -inset-6 bg-pshl-electric/10 blur-3xl opacity-50 pointer-events-none" />
              <motion.img
                src="/pshl8-brand.png"
                alt="PSHL8 official brand mark"
                draggable={false}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full h-auto select-none border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* COMMERCIAL VISION */}
      <section data-testid="section-vision" className="section">
        <div className="pshl-container grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-4">The Vision</div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[0.95] tracking-tighter">
              Commercializing <br /> Skate Hockey <br /> <span className="text-gold-metallic">In India.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-base md:text-lg text-white/80 leading-relaxed">
              PSHL is building a professional sports ecosystem around skate hockey through city-based
              franchises, sponsorships, youth academies, tournaments, digital content, merchandise, and
              media partnerships.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Franchise-led city expansion",
                "Premium sponsor inventory",
                "Grassroots academy network",
                "Original media + OTT pipeline",
                "Player draft + scouting system",
                "Merch + arena commerce",
              ].map((p) => (
                <div key={p} className="flex items-center gap-3 border border-white/8 bg-white/[0.02] px-4 py-3">
                  <div className="h-1.5 w-1.5 bg-pshl-electric" />
                  <span className="text-sm text-white/85">{p}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 pt-4">
              <a href="#" data-testid="download-sponsor-deck" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 hover:brightness-110 transition-all">
                <Download className="h-3.5 w-3.5" /> Sponsorship Deck
              </a>
              <a href="#" data-testid="download-franchise-deck" className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 transition-all">
                <Download className="h-3.5 w-3.5" /> Franchise Deck
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PSHL8 PILLARS — Bento Grid */}
      <section data-testid="section-pillars" className="section relative">
        <div className="absolute inset-0 opacity-10" style={{ background: "radial-gradient(800px 400px at 50% 0%, rgba(0,85,255,0.4), transparent 70%)" }} />
        <div className="relative pshl-container">
          <div className="flex items-end justify-between mb-8 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">The PSHL8 Framework</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Eight Pillars. <span className="text-white/40">One League.</span></h2>
            </div>
            <Link to="/about" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-display text-white/70 hover:text-pshl-electric">
              Read Manifesto <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] md:auto-rows-[240px] gap-3 md:gap-4">
            {PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[p.key];
              const featured = i === 0 || i === 3 || i === 5;
              return (
                <motion.div
                  key={p.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  whileHover={{ y: -3 }}
                  data-testid={`pillar-${p.key}`}
                  className={`relative group border border-white/10 hover:border-pshl-electric overflow-hidden transition-colors ${
                    featured ? "md:row-span-2" : ""
                  }`}
                >
                  {/* Background image — AI-generated PSHL pillar art with Unsplash fallback */}
                  <PillarBg src={p.image} fallbackSrc={p.fallback} />
                  {/* Cinematic overlay: dark bottom + tint */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020A1A] via-[#020A1A]/65 to-[#020A1A]/30" />
                  <div className="absolute inset-0 bg-gradient-to-br from-pshl-electric/15 via-transparent to-pshl-red/15 mix-blend-screen opacity-70 group-hover:opacity-100 transition-opacity" />
                  {/* Speed lines for energy on featured tiles */}
                  {featured && <div className="absolute inset-0 speed-lines opacity-25" aria-hidden="true" />}

                  {/* Content */}
                  <div className="relative h-full p-5 md:p-6 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className="h-10 w-10 grid place-items-center bg-pshl-electric/20 backdrop-blur-sm border border-pshl-electric/50">
                        <Icon className="h-5 w-5 text-pshl-electric" strokeWidth={2.2} />
                      </div>
                      <div className="font-mono text-[10px] tracking-[0.25em] text-white/65">0{i + 1}</div>
                    </div>
                    <div>
                      <div className="font-display text-3xl md:text-4xl text-white tracking-tight leading-none drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
                        {p.title}
                      </div>
                      {featured && (
                        <p className="text-xs md:text-sm text-white/85 mt-2 leading-relaxed max-w-[28ch] drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)]">
                          {p.desc}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAMS */}
      <section data-testid="section-teams" className="section">
        <div className="pshl-container">
          <div className="flex items-end justify-between mb-8 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Inaugural Season</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">The Franchise <span className="text-pshl-electric">Eight.</span></h2>
            </div>
            <Link to="/teams" data-testid="teams-view-all" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-display text-white/80 hover:text-pshl-electric">
              All Teams <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {teams.map((t, i) => <TeamCard key={t.slug} team={t} index={i} />)}
          </div>
        </div>
      </section>

      {/* MOST SHARED THIS WEEK — only renders when there's any data */}
      {trending.length > 0 && (
        <section data-testid="section-trending" className="section bg-[#08090E]">
          <div className="pshl-container">
            <div className="flex items-end justify-between mb-6 gap-6">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-pshl-red font-display mb-3">
                  <Fire className="h-3.5 w-3.5" />
                  <span>Most Shared · Last 7 Days</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">
                  Trending <span className="text-pshl-electric">Franchises.</span>
                </h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {trending.map((row, i) => {
                const t = row.team;
                if (!t) return null;
                return (
                  <motion.div
                    key={`${row.kind}-${row.target_id}`}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    data-testid={`trending-${row.target_id}`}
                  >
                    <Link to={`/teams/${t.slug}`} className="group block relative bg-pshl-surface border border-white/10 hover:border-pshl-red p-5 transition-colors overflow-hidden">
                      <div className="absolute -bottom-12 -right-12 h-36 w-36 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: t.primary }} />
                      <div className="relative flex items-start justify-between">
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.25em] text-pshl-red">#{String(i + 1).padStart(2, "0")}</div>
                          <div className="font-display text-pshl-red text-2xl mt-1 leading-none">{row.count}</div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mt-1">shares</div>
                        </div>
                        <TeamLogo team={t} size={56} />
                      </div>
                      <div className="relative mt-5">
                        <div className="font-display text-lg text-white leading-tight">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mt-1">{t.city}</div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORSHIP */}
      <section data-testid="section-sponsorship" className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="grid lg:grid-cols-12 gap-8 mb-8">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Commercial Tracks</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Sponsor PSHL. <span className="text-gold-metallic">Reach 1.4B.</span></h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-white/75 leading-relaxed">
                Premium sponsor inventory across the league, teams, jerseys, arenas and broadcast.
                Built for brands that want CSR, youth engagement, and pan-India visibility.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiers.map((p, i) => <SponsorPackageCard key={p.id} pkg={p} index={i} onCta={() => {
              const el = document.getElementById("home-leads");
              el?.scrollIntoView({ behavior: "smooth" });
            }} />)}
          </div>
        </div>
      </section>

      {/* FRANCHISE OWNERSHIP */}
      <section id="franchise" data-testid="section-franchise" className="section relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "linear-gradient(135deg, rgba(0,85,255,0.18), transparent 40%), linear-gradient(225deg, rgba(255,215,0,0.12), transparent 50%)"
        }} />
        <div className="relative pshl-container grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Franchise Investor</div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Own a <br /><span className="text-gold-metallic">PSHL Franchise.</span></h2>
            <p className="mt-5 text-white/75 leading-relaxed max-w-xl">
              City and state-based team ownership built like an IPL franchise. Long-term revenue
              streams across sponsorships, merchandise, academies, player IP, and media.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {[
                "City/state team rights",
                "Local sponsorship revenue",
                "Merch & arena commerce",
                "Academy network monetization",
                "Player IP + transfer system",
                "Broadcast revenue share",
                "CSR alignment", "Long-term equity",
              ].map((b) => (
                <div key={b} className="flex items-start gap-3 border border-white/8 bg-white/[0.02] px-4 py-3">
                  <Shield className="h-4 w-4 mt-0.5 text-pshl-gold" />
                  <span className="text-sm text-white/85">{b}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="franchise-apply-cta" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-6 py-4 hover:brightness-110 transition-all">
                Apply for Franchise Ownership <ChevronRight className="h-4 w-4" />
              </Link>
              <a href="#" data-testid="franchise-deck-cta" className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-4">
                <Download className="h-3.5 w-3.5" /> Franchise Deck
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-pshl-surface border border-white/10 p-6 md:p-8">
              <div className="font-display text-xs uppercase tracking-[0.22em] text-pshl-electric">Investor Snapshot</div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                {[
                  ["Format", "City-Franchise"],
                  ["Season", "Annual"],
                  ["Rights", "10-year term"],
                  ["Revenue", "Multi-stream"],
                  ["Markets", "Tier 1 + 2"],
                  ["Demographics", "16–45 yrs"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">{k}</div>
                    <div className="font-display text-xl text-white mt-1">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border-t border-white/10 pt-6 text-sm text-white/70 leading-relaxed">
                PSHL8 is structured for franchise investors who want a long-term ownership position
                in India’s emerging premium sports portfolio. Talk to our partnerships team for the
                detailed prospectus.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCHEDULE & STANDINGS */}
      <section data-testid="section-match-center" className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="flex items-end justify-between mb-6 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Match Center</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Schedule. <span className="text-white/40">Scores.</span> Standings.</h2>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <ScheduleTable schedule={schedule.slice(0, 5)} teamsBySlug={teamsBySlug} />
            <StandingsTable standings={standings} teamsBySlug={teamsBySlug} />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/schedule" className="inline-flex items-center gap-2 border border-white/15 hover:border-pshl-electric text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 transition-colors">
              Full Schedule <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/standings" className="inline-flex items-center gap-2 border border-white/15 hover:border-pshl-electric text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 transition-colors">
              Standings <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* PLAYERS & DRAFT */}
      <section data-testid="section-players" className="section">
        <div className="pshl-container">
          <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Players & Draft</div>
          <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter mb-8">From Talent <span className="text-pshl-electric">to Trophy.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { title: "Player Registration", desc: "Open trials and online registration for athletes 16–35." },
              { title: "Talent Scouting", desc: "Scouts across 12 cities + academy partner network." },
              { title: "Draft / Auction System", desc: "Live draft and auction format inspired by global pro leagues." },
              { title: "PSHL8 Player Rating", desc: "Proprietary 8-pillar rating across the league." },
              { title: "Academy Pathway", desc: "Direct pathway from PSHL academies to pro contracts." },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`player-block-${i}`}
                className="bg-pshl-surface border border-white/8 hover:border-pshl-electric transition-colors p-5 group"
              >
                <div className="font-mono text-[10px] tracking-[0.25em] text-pshl-electric">P0{i + 1}</div>
                <div className="mt-3 font-display text-lg text-white">{b.title}</div>
                <div className="mt-2 text-sm text-white/65 leading-relaxed">{b.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section data-testid="section-news" className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="flex items-end justify-between mb-6 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">News & Media</div>
              <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">From the <span className="text-white/40">Rink.</span></h2>
            </div>
            <Link to="/news" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-display text-white/80 hover:text-pshl-electric">
              All News <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {news.slice(0, 6).map((n, i) => (
              <motion.article
                key={n.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -3 }}
                data-testid={`news-card-${n.id}`}
                className="group bg-pshl-surface border border-white/8 hover:border-pshl-electric transition-colors overflow-hidden flex flex-col"
              >
                <Link to={`/news/${n.id}`} className="flex flex-col h-full">
                  <div
                    className="h-44 bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${n.image})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-pshl-surface to-transparent" />
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] font-display font-semibold bg-pshl-electric text-white px-2 py-1">
                      {n.category}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">{n.date}</div>
                    <h3 className="mt-2 font-display text-lg text-white leading-tight group-hover:text-pshl-electric transition-colors">{n.title}</h3>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed flex-1">{n.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-display uppercase tracking-[0.18em] text-white/80 group-hover:text-pshl-electric">
                      Read Story <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ACADEMIES */}
      <section data-testid="section-academy" className="section">
        <div className="pshl-container grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Academy & Youth</div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Building the <br /><span className="text-pshl-electric">Future of Skate Hockey.</span></h2>
            <p className="mt-5 text-white/75 leading-relaxed">
              From schoolyard rinks to pro contracts — PSHL is building India’s most ambitious
              skate hockey pathway with grassroots academies, district leagues, certified
              coaching and talent ID.
            </p>
            <Link to="/academies" data-testid="academy-cta" className="mt-7 inline-flex items-center gap-2 bg-pshl-electric hover:bg-pshl-electric/90 text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-3.5 transition-all">
              Explore Academy Programs <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              "School Programs", "District Leagues", "Youth Camps",
              "Coaching Certifications", "Talent Identification", "Grassroots Tournaments",
            ].map((s, i) => (
              <div key={s} className="bg-pshl-surface border border-white/8 p-5">
                <div className="font-mono text-[10px] tracking-[0.25em] text-pshl-electric">A0{i + 1}</div>
                <div className="mt-3 font-display text-xl text-white">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section id="home-leads" data-testid="section-leads" className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Get Involved</div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Sponsor. Own. Play. <span className="text-pshl-electric">Partner.</span></h2>
            <p className="mt-3 text-white/70 max-w-2xl">Tell us your interest — sponsor, franchise investor, player, academy or media. Our partnerships team responds within 24 hours.</p>
          </div>
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <LeadForm defaultInterest="sponsor" />
            </div>
            <div className="lg:col-span-5 space-y-4">
              <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" data-testid="leads-whatsapp" className="block bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric">WhatsApp</div>
                <div className="font-display text-2xl text-white mt-2">Chat with the PSHL team</div>
                <div className="text-sm text-white/65 mt-2">Quick partnership conversation? Ping us on WhatsApp.</div>
              </a>
              <a href={`mailto:${BRAND.email}`} className="block bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric">Email</div>
                <div className="font-display text-2xl text-white mt-2">{BRAND.email}</div>
                <div className="text-sm text-white/65 mt-2">Send your brief or RFP directly to our partnerships team.</div>
              </a>
              <div className="bg-pshl-surface border border-white/10 p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric">Decks</div>
                <div className="font-display text-2xl text-white mt-2">Download our Pitch Decks</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href="#" data-testid="leads-deck-sponsor" className="text-xs font-display uppercase tracking-[0.18em] px-4 py-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black hover:brightness-110">Sponsorship</a>
                  <a href="#" data-testid="leads-deck-franchise" className="text-xs font-display uppercase tracking-[0.18em] px-4 py-2 border border-white/20 text-white hover:border-white">Franchise</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
